import './components/commons.scss';
import './components/FluidImageLoading.scss';
import './components/HeroSection.scss';
import './components/HowToCreate.scss';
import './components/IdeasAndAdvice.scss';
import './components/KeyBenefitsSection.scss';
import './components/NavigationTabs.scss';
import './components/PricingSection.scss';
import './components/TakeYourBusinessFurther.scss';
import './components/TestimonialsSection.scss';
import './components/WebsitesTemplates.scss';
import './components/WixFeatures.scss';
import './components/Cta.scss';

import React, { lazy, Suspense, useContext, useEffect, useRef } from 'react';

import { Box, BoundedContent, Divider, Link, useScreenClass } from '@vp/swan';

import { SEOContainer } from '@vp/digital-seo-lib';
import { useLocale } from '@vp/digital-locale-lib';
import { getLogger } from '@vp/digital-logging-lib';
import { isProduction } from '@vp/digital-environment-lib';
import { useConfiguredRollbar } from '@vp/digital-rollbar-lib';
import { initTrackingWithKeys } from '@vp/digital-tracking-lib';
import {
  getHowToCreateWebsiteUrl,
  getSmallBusinessWebsiteUrl,
  getSevenStunningWebDesignTrendsUrl,
  getCheatSheetForSmallBusinessSeoUrl,
} from '@vp/digital-url-service-lib/src/partners/partnerUrlGenerator';
import { WIX_WEBSITES_MPV_ID } from '@vp/digital-product-availability-lib';
import { TokenReplacer, LinkFactory } from '@vp/digital-token-replacer-lib';

import {
  experimentLocales,
  experimentKeyEnum,
  variationKeyEnum,
} from '../../enums/experimentKey';
import {
  trackFreeWebsitesSectionLinkClick,
  trackTemplatesIndustrySectionLinkClick,
  trackNavigationTabsSectionLinkClick,
  trackFindYourDomainLinkClick,
  trackUpgradeToWixPlusLinkClick,
  trackCreateYourLogoLinkClick,
  trackHowToCreateWebsiteLinkClick,
  trackSmallBusinessWebsiteDesignLinkClick,
  trackSevenWebDesignTrendsLinkClick,
  trackCheatSheetForBusinessSeoLinkClick,
  trackPlansSectionGetStartedClick,
} from './WixWebsitesAnalytics';
import Loading from '../common/Loading';
import { redirect } from '../../helpers/urlsHelper';
import { WixPageAnalyticsContext } from '../../helpers/WixPageAnalyticsProvider';

import HeroSection from './components/HeroSection';
import NavigationTabs from './components/NavigationTabs';

const KeyBenefitsSection = lazy(
  () => import('./components/KeyBenefitsSection')
);
const TestimonialsSection = lazy(
  () => import('./components/TestimonialsSection')
);
const TakeYourBusinessFurther = lazy(
  () => import('./components/TakeYourBusinessFurther')
);
const HowToCreate = lazy(() => import('./components/HowToCreate'));
const WixFeatures = lazy(() => import('./components/WixFeatures'));
const FeaturesCta = lazy(() => import('./components/FeaturesCta'));
const PricingSection = lazy(() => import('./components/PricingSection'));
const IdeasAndAdvice = lazy(() => import('./components/IdeasAndAdvice'));
const WebsitesFAQs = lazy(() => import('../common/WebsitesFAQs/WebsitesFAQs'));
const WebsitesTemplates = lazy(() => import('./components/WebsitesTemplates'));
const TemplatesCta = lazy(() => import('./components/TemplatesCta'));
const HowToCreateCta = lazy(() => import('./components/HowToCreateCta'));

const WixPdpCWebsitesPage = ({ pageContext }: any) => {
  useConfiguredRollbar();

  const { current: logger } = useRef(
    getLogger({ page: WIX_WEBSITES_MPV_ID + 'CNewDesign' })
  );

  const {
    trackProductLoad,
    fireStatsigImpression,
    trackToggleClickedEvent,
    trackingAssetName,
  } = useContext(WixPageAnalyticsContext);
  const { languageCode } = useLocale();

  const isMobile = useScreenClass() === 'xs';

  const {
    locale,
    packageGridPlans,
    customerCarePhone,
    pageAnalyticsData,
    pageData: content,
    commonLocalizedUrls,
    mpv: { version: productVersion },
    urlWixPartnerGetStartedTnCAcceptedUrls,
  } = pageContext;

  const urlWixPartnerGetStartedTncAcceptedUrl = isProduction()
    ? urlWixPartnerGetStartedTnCAcceptedUrls.prod
    : urlWixPartnerGetStartedTnCAcceptedUrls.qa;

  useEffect(() => {
    content.newFaqsData = {
      ...content.newFaqs,
      features: content.newFaqs.features.map((feature: any) => ({
        title: feature.title,
        description: (
          <TokenReplacer
            template={feature.description}
            replacements={{
              carePhoneNumber: (
                <Link href={`tel:${customerCarePhone?.longFormatPhoneNumber}`}>
                  {customerCarePhone?.phoneNumber}
                </Link>
              ),
              link: LinkFactory({
                domainsPdpUrl: commonLocalizedUrls.domainsPdpUrl,
                anonymousPlanPickerUrl: commonLocalizedUrls.wixPlansUrl,
                ninetyNineDesignWixUrl: content.ninetyNineDesignWixUrl.title,
                customerCareHelpCenterUrl:
                  commonLocalizedUrls.customerCareHelpCenterPageUrl,
                customerCareHelpCenterDomainNameUrl:
                  commonLocalizedUrls.customerCareHelpCenterPageUrl +
                  content.customerCareHelpCenterDomainNameUrl.title,
              }),
            }}
            convertMarkdown
          />
        ),
      })),
    };
  }, []);

  const handleWixPlansPage = () => {
    try {
      logger.debug('Clicking PDP C See Premium Plans');
      window.location.href = commonLocalizedUrls.wixPlansUrl;
    } catch (error) {
      logger.error('Error handling Wix Plans Page:', error);
    }
  };

  const handleGetStartedNavigation = () => {
    try {
      logger.debug('Clicking PDPC Get Started to Wix IntroFunnel Navigate');
      let url = urlWixPartnerGetStartedTncAcceptedUrl;
      if (url === undefined) {
        logger.error(
          'Error Clicking PDPC Get Started to Wix IntroFunnel Navigate Url is undefined ',
          null
        );
      }
      if (isMobile) {
        url += '&deviceType=mobile';
      }
      redirect(url);
    } catch (error) {
      logger.error('Error handling Get Started Navigation:', error);
    }
  };

  const handleNavigation = (url: any, trackingFn: any) => {
    trackingFn(url);
    setTimeout(() => {
      window.location.href = url;
    }, 100);
  };

  const handleHowToCreateWebsiteNavigation = () =>
    redirectToPage(getHowToCreateWebsiteUrl);

  const handleSmallBusinessWebsiteNavigation = () =>
    redirectToPage(getSmallBusinessWebsiteUrl);

  const handleSevenWebsiteDesignTrendsNavigation = () =>
    redirectToPage(getSevenStunningWebDesignTrendsUrl);

  const handleCheatSheetSeoNavigation = () =>
    redirectToPage(getCheatSheetForSmallBusinessSeoUrl);

  const redirectToPage = (getUrlFunction: (languageCode: string) => string) => {
    const url = getUrlFunction(languageCode);
    if (url) {
      window.location.href = url;
    } else {
      logger.error('URL not found for languageCode:', languageCode);
    }
  };

  // Analytics
  useEffect(() => {
    logger.debug('Initializing page tracking');
    initTrackingWithKeys(
      pageContext.locale,
      pageContext.trackingKeys,
      trackingAssetName
    );
    trackProductLoad();
    if (experimentLocales.includes(pageContext.locale.toLowerCase())) {
      fireStatsigImpression(
        experimentKeyEnum.WIX_WEBSITES_PDP_PERFORMANCE_ANALYSIS,
        variationKeyEnum.WIX_WEBSITES_PDP_TEST_VARIATION
      );
    }
  }, [pageContext.locale, pageContext.trackingKeys]);

  return (
    <>
      <SEOContainer content={content.seo} />

      {/* hero */}
      <HeroSection
        pageContext={pageContext}
        content={content.newMainHero}
        handleGetStartedNavigation={handleGetStartedNavigation}
      />

      {/* navigation */}
      <NavigationTabs
        content={{
          nav: content.navigationTabs,
          salesBanner: content.salesBanner,
        }}
        locale={locale}
        accessibility={content.accessibility}
        primaryCtaClick={() => {
          trackNavigationTabsSectionLinkClick(
            urlWixPartnerGetStartedTncAcceptedUrl
          );
          handleGetStartedNavigation();
        }}
      />

      {/* features */}
      <BoundedContent as="section" id="features">
        <Suspense fallback={<Loading />}>
          <Box py={{ xs: 6, sm: 6, md: 9 }}>
            <WixFeatures
              content={content.wixFeaturesSubHero}
              accessibility={content.accessibility}
            />
          </Box>
        </Suspense>
      </BoundedContent>
      {/* cta  features */}
      <BoundedContent>
        <Suspense fallback={<Loading />}>
          <Box pt={{ xs: 5, sm: 5, md: 5, xl: 8 }} pb={{ xs: 6, sm: 6, md: 9 }}>
            <FeaturesCta
              content={content.cta}
              primaryCtaClick={() => {
                trackTemplatesIndustrySectionLinkClick(
                  urlWixPartnerGetStartedTncAcceptedUrl
                );
                handleGetStartedNavigation();
              }}
            />
          </Box>
        </Suspense>
      </BoundedContent>

      <BoundedContent>
        <Divider />
      </BoundedContent>

      {/* templates */}
      <BoundedContent as="section" id="templates">
        <Suspense fallback={<Loading />}>
          <Box pt={{ xs: 6, sm: 6, md: 9 }}>
            <WebsitesTemplates
              content={content.templates}
              accessibility={content.accessibility}
            />
          </Box>
        </Suspense>
      </BoundedContent>

      {/* cta */}
      <BoundedContent>
        <Suspense fallback={<Loading />}>
          <Box pt={7} pb={{ xs: 6, sm: 6, md: 9 }}>
            <TemplatesCta
              content={content.cta}
              primaryCtaClick={() => {
                trackTemplatesIndustrySectionLinkClick(
                  urlWixPartnerGetStartedTncAcceptedUrl
                );
                handleGetStartedNavigation();
              }}
            />
          </Box>
        </Suspense>
      </BoundedContent>

      {/* key benefits */}
      <BoundedContent
        as="section"
        id="key-benefits"
        backgroundColor="warm-white"
      >
        <Suspense fallback={<Loading />}>
          <Box py={{ xs: 6, sm: 6, md: 9 }}>
            <KeyBenefitsSection
              content={content.keyBenefits}
              accessibility={content.accessibility}
            />
          </Box>
        </Suspense>
      </BoundedContent>

      {/* Pricing section */}
      <BoundedContent as="section" id="pricing">
        <Suspense fallback={<Loading />}>
          <Box py={{ xs: 6, sm: 6, md: 9 }}>
            <PricingSection
              packageGridPlans={packageGridPlans}
              locale={locale}
              productVersion={productVersion}
              content={content.pricingHeroGroup}
              handleGetStartedNavigation={() => {
                trackPlansSectionGetStartedClick(
                  urlWixPartnerGetStartedTncAcceptedUrl
                );
                handleGetStartedNavigation();
              }}
              handleWixPlansPage={handleWixPlansPage}
              trackToggleClickedEvent={trackToggleClickedEvent}
              pageAnalyticsData={pageAnalyticsData}
              accessibility={content.accessibility}
            />
          </Box>
        </Suspense>
      </BoundedContent>

      {/* How to create */}
      <BoundedContent darkMode id="how-it-works" backgroundColor="midnight">
        <Suspense fallback={<Loading />}>
          <Box pt={{ xs: 6, sm: 6, md: 9 }} as="section">
            <HowToCreate
              content={content.newCreateSubHero}
              accessibility={content.accessibility}
            />
          </Box>
        </Suspense>
      </BoundedContent>

      {/* cta */}
      <BoundedContent darkMode backgroundColor="midnight">
        <Suspense fallback={<Loading />}>
          <Box pb={{ xs: 6, sm: 6, md: 9 }}>
            <HowToCreateCta
              content={content.cta}
              primaryCtaClick={() => {
                trackFreeWebsitesSectionLinkClick(
                  urlWixPartnerGetStartedTncAcceptedUrl
                );
                handleGetStartedNavigation();
              }}
            />
          </Box>
        </Suspense>
      </BoundedContent>

      {/* Testimonials section */}
      <BoundedContent as="section" id="reviews">
        <Suspense fallback={<Loading />}>
          <Box py={{ xs: 6, sm: 6, md: 9 }}>
            <TestimonialsSection
              content={content.testimonials}
              accessibility={content.accessibility}
            />
          </Box>
        </Suspense>
      </BoundedContent>

      {/* Take your business further section */}
      <BoundedContent as="section">
        <Suspense fallback={<Loading />}>
          <Box pt={{ xs: 0, sm: 8, md: 8, lg: 8, xl: 8 }}>
            <TakeYourBusinessFurther
              content={content.takeYourBusinessFurther}
              locale={locale.toLowerCase()}
              vistaWixDomainsCtaClick={() =>
                handleNavigation(
                  commonLocalizedUrls.domainsPdpUrl,
                  trackFindYourDomainLinkClick
                )
              }
              logoMakerCtaClick={() =>
                handleNavigation(
                  commonLocalizedUrls.logoMakerLink,
                  trackCreateYourLogoLinkClick
                )
              }
              vistaWixPlusCtaClick={() =>
                handleNavigation(
                  commonLocalizedUrls.wixPlusLink,
                  trackUpgradeToWixPlusLinkClick
                )
              }
              accessibility={content.accessibility}
            />
          </Box>
        </Suspense>
      </BoundedContent>

      {/* Websites Ideas and Advice section */}
      {locale.toLowerCase() === 'en-us' && (
        <BoundedContent as="section" backgroundColor="warm-white">
          <Suspense fallback={<Loading />}>
            <Box pt={{ xs: 6, sm: 6, md: 9 }}>
              <IdeasAndAdvice
                content={content.ideasAndAdvice}
                articleOne={() => {
                  trackHowToCreateWebsiteLinkClick(
                    getHowToCreateWebsiteUrl(languageCode)
                  );
                  handleHowToCreateWebsiteNavigation();
                }}
                articleTwo={() => {
                  trackSmallBusinessWebsiteDesignLinkClick(
                    getSmallBusinessWebsiteUrl(languageCode)
                  );
                  handleSmallBusinessWebsiteNavigation();
                }}
                articleThree={() => {
                  trackSevenWebDesignTrendsLinkClick(
                    getSevenStunningWebDesignTrendsUrl(languageCode)
                  );
                  handleSevenWebsiteDesignTrendsNavigation();
                }}
                articleFour={() => {
                  trackCheatSheetForBusinessSeoLinkClick(
                    getSevenStunningWebDesignTrendsUrl(languageCode)
                  );
                  handleCheatSheetSeoNavigation();
                }}
                accessibility={content.accessibility}
              />
            </Box>
          </Suspense>
        </BoundedContent>
      )}

      {/* Wix PDP-A faq */}
      <BoundedContent as="section" backgroundColor="warm-white">
        <Suspense fallback={<Loading />}>
          <Box py={{ xs: 6, sm: 6, md: 9 }}>
            <WebsitesFAQs content={content.newFaqsData} />
          </Box>
        </Suspense>
      </BoundedContent>
    </>
  );
};

export default WixPdpCWebsitesPage;
